import { createStore } from 'vuex'

export default createStore({
  state: {
    selectedValues: {
      challenges: [],
      industry: "",
      size: "> 50",
      maturity: 0,
      role: "",
      integrations: [],
      priorities: [],
      timeframe: "Right away",
      approach: ""
    },
    isSelected: false,
    pages: {
      intro: {
        component: 'Intro',
        title: 'Take your digital evolution assessment',
        subtitle: 'Get tailored recommendations on the right transformative technology solutions for your business'
      },
      challenges: {
        title: 'What are some of your organization’s biggest content and process-related challenges?',
        subtitle: 'Select and drag your top three',
        component: 'DragAndDrop',
        limit: 3,
        content: [
          {
            img: '/page-2/solution-configurator-page-2-problems-01.svg',
            alt: 'Modernizing or digitally transforming our operations',
            title: 'Modernize or digitally transform your operations',
            category: 'challenges',
            value: 'modernizing',
            id: 'challenges-card-1'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-02.svg',
            alt: 'Supporting customer retention and acquisition',
            title: 'Support customer retention and acquisition',
            category: 'challenges',
            value: 'supporting',
            id: 'challenges-card-2'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-03.svg',
            alt: 'Information silos limit the ability to find and access business-critical information',
            title: 'Improve your ability to find and access business-critical information',
            category: 'challenges',
            value: 'businessCriticalInformation',
            id: 'challenges-card-3'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-04.svg',
            alt: 'Transitioning to a more remote workforce',
            title: 'Ease the transition to a more remote workforce',
            category: 'challenges',
            value: 'transitioning',
            id: 'challenges-card-4'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-05.svg',
            alt: 'Costs and inefficiencies due to manual processes',
            title: 'Address unnecessary costs and inefficiencies due to manual processes',
            category: 'challenges',
            value: 'costAndInefficiences',
            id: 'challenges-card-5'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-06.svg',
            alt: 'Information security and compliance',
            title: 'Improve information security and compliance',
            category: 'challenges',
            value: 'informationSecurityAndCompliance',
            id: 'challenges-card-6'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-07.svg',
            alt: 'Wrangling numerous content management systems or partners',
            title: 'Better manage your content management systems or partners',
            category: 'challenges',
            value: 'contentManagement',
            id: 'challenges-card-7'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-08.svg',
            alt: 'Struggling with legacy systems that lack scalability, flexibility and features to support business growth',
            title: 'Replace legacy systems that lack scalability, flexibility and features to support business growth',
            category: 'challenges',
            value: 'legacySystems',
            id: 'challenges-card-8'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-09.svg',
            alt: 'Business requirements outpacing IT resources',
            title: 'Better equip your IT department to keep pace with business requirements',
            category: 'challenges',
            value: 'itResources',
            id: 'challenges-card-9'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-10.svg',
            alt: 'Supporting employee retention, engagement and productivity',
            title: 'Improve employee retention, engagement and productivity',
            category: 'challenges',
            value: 'employee',
            id: 'challenges-card-10'
          },
          {
            img: '/page-2/solution-configurator-page-2-problems-11.svg',
            alt: 'Managing our technology budget',
            title: 'Get a handle on your technology budget',
            category: 'challenges',
            value: 'technologyBudget',
            id: 'challenges-card-11'
          },
        ]
      },
      industries: {
        title: 'Which industry do you work in?',
        subtitle: 'Select one',
        component: 'RadioCarousel',
        isRadio: true,
        content: [
          {
            img: '/page-3/solution-configurator-page-3-industries-01.svg',
            alt: 'Healthcare',
            category: 'industry',
            value: 'healthcare',
            result: [
              `3,700 healthcare customers`,
              `Hyland solutions for healthcare`,
              `<p>As a healthcare organization, nothing matters more to you than your patients. Everything you do is geared towards ensuring you deliver the best care and patient experience possible. Hyland provides connected healthcare solutions that make patient content and images readily accessible, allowing your care teams to focus on their patients rather than searching for the information they need to make vital clinical decisions.</p>
              <p>Hyland Healthcare is the only provider that brings together content services and enterprise imaging into a truly connected healthcare platform.</p> 
              <p><a class="btn btn-orange" href="https://www.hyland.com/en/learn/healthcare/connected-healthcare-platform-ebook" target="_blank">Learn more about why that matters. <span class="fas fa-angle-double-right"></span></a></p>`,
              `healthcare organization`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-02.svg',
            alt: 'Higher Education',
            category: 'industry',
            value: 'higherEducation',
            result: [
              `1,040 education customers`,
              `Hyland solutions for higher education`,
              `<p>"As a higher education institution, you’re always looking to enhance the student, alumni, donor and other stakeholders’ experience. That’s why today, just going paperless isn’t enough. Your content needs to be streamlined and have a clean, clear process.</p>
              <p>Hyland’s market-leading suite of content services solutions enables institutions like yours to leverage the data in your current technology stacks to give staff a complete view of the student; then, they can focus on high-value engagement efforts that will further enhance the experience of your campus community.</p>
              <p><a class="btn btn-orange" href="https://www.hyland.com/en/learn/higher-education/digital-transformation-ebook" target="_blank">Learn how you can unlock the value of content services. <span class="fas fa-angle-double-right"></span></a></p>`,
              `institution`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-03.svg',
            alt: 'Insurance',
            category: 'industry',
            value: 'insurance',
            result: [
              `535 insurance customers`,
              `Hyland solutions for insurance`,
              `<p>What if you could make better underwriting decisions? Process insured claims faster? Help your customers get back on their feet, back on the job and back to everyday life sooner than they ever imagined? Hyland insurance software solutions complement your core business systems, providing employees with a complete view of the information they need, when and where they need it.</p>
              <p><a class="btn btn-orange" href="https://www.hyland.com/en/learn/insurance/complete-view-ebook" target="_blank">Learn how gaining a complete view of your customer can strengthen your customer relationships. <span class="fas fa-angle-double-right"></span></a></p>`,
              `insurance company`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-04.svg',
            alt: 'Financial Services',
            category: 'industry',
            value: 'financialServices',
            result: [
              `3,300 financial services customers`,
              `Hyland solutions for financial services`,
              `<p>In today’s digital-first world, financial services organizations are facing new types of competition and consumers that expect fast and easy digital connections. Hyland’s content services platform can help your financial institution drive digital transformation strategies, meet ever-changing compliance requirements and deliver outstanding customer experiences. With scalable and configurable capabilities, our solutions can help you chart a course for a successful digital journey.</p>
              <p><a class="btn btn-orange" href="https://www.hyland.com/en/learn/financial-services/fs-8-trends-article-2019" target="_blank">Learn why banks need to change their CX strategy. <span class="fas fa-angle-double-right"></span></a></p>`,
              `financial services organization`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-05.svg',
            alt: 'Government',
            category: 'industry',
            value: 'government',
            result: [
              `1,815 government customers`,
              `Hyland solutions for government`,
              `<p>There has never been a greater need for technology to keep government functioning and ensure the delivery of mission-critical services without disruption. Time-consuming, paper-based and email/spreadsheet processes make it difficult for agencies and departments to efficiently deliver services and timely responses.</p>
              <p>Hyland’s content services platform can help you speed up processes and increase efficiencies while improving transparency and public trust. The platform’s scalable, configurable capabilities help each level of government meet constituent needs while laying the foundation for a streamlined, digital government.</p>
              <p><a class="btn btn-orange" href="Learn what three key priorities you should be focusing on." target="_blank">Learn what three key priorities you should be focusing on. <span class="fas fa-angle-double-right"></span></a></p>`,
              `goverment agency or department`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-07.svg',
            alt: 'Other',
            category: 'industry',
            value: 'other',
            result: [
              `half of 2020 Fortune 100 companies`,
              `the Hyland platform`,
              `<p>Hyland is focused on empowering organizations with the most flexible, extensible and configurable cloud content services platform on the planet. We’re ready to take your customer and employee experience, digital transformation, data security and compliance to the next level.</p> 
              <p>We’re not the solution—you and your employees are. We simply hand you the tools you need to create exceptional outcomes tailored to your needs and industry. Some benefits of our solutions include:</p>
              <ul>
                <li>Intelligent automation so your team can focus on high-value work</li>
                <li>Agile and adaptive to support your evolving requirements</li>
                <li>Low-code and configurable without a developer’s background</li>
                <li>Designed to streamline workflows and business processes</li>
              </ul>
              <p><a class="btn btn-orange" href="https://www.hyland.com/-/media/new-brand/pdfs/ebook/prod-ebook-hyland-101.pdf?la=en" target="_blank">Discover more about our content services platform in this eBook. <span class="fas fa-angle-double-right"></span></a></p>`,
              `organization`
            ],
          },
          {
            img: '/page-3/solution-configurator-page-3-industries-06.svg',
            alt: 'Manufacturing',
            category: 'industry',
            value: 'manufacturing',
            result: [
              `half of 2020 Fortune 100 companies`,
              `Hyland solutions for manufacturing`,
              `<p>As a manufacturing organization, you manage a complex system from operations to quality control to supply chain, HR and more. That’s why it’s important to extend the technological innovation you’ve implemented on the manufacturing floor to your entire organization’s processes.</p>
              <p>Hyland’s platform enables manufacturers to meet the needs of each department—including accounting, finance, HR and legal—by managing content in a central repository for more streamlined operations from anywhere, on any device.</p>
              <p><a class="btn btn-orange" href="https://www.hyland.com/en/learn/commercial/IDC-manufacturing-wp" target="_blank">Learn what IDC says about manufacturing in the digital age. <span class="fas fa-angle-double-right"></span></a></p>`,
              `manufacturing organization`
            ],
          },
        ],
      },
      size: {
        title: 'How many employees work at your organization? ',
        subtitle: 'Select size range',
        component: 'RangeSelector',
        content: [
          {
            value: '> 50',
            img: '/page-4/solution-configurator-page-4-buildings-01.svg',
          },
          {
            value: '51-200',
            img: '/page-4/solution-configurator-page-4-buildings-02.svg',
          },
          {
            value: '201-500',
            img: '/page-4/solution-configurator-page-4-buildings-03.svg',
          },
          {
            value: '501-1000',
            img: '/page-4/solution-configurator-page-4-buildings-04.svg',
          },
          {
            value: '1001-5000',
            img: '/page-4/solution-configurator-page-4-buildings-05.svg',
          },
          {
            value: '5001-10,000',
            img: '/page-4/solution-configurator-page-4-buildings-06.svg',
          },
          {
            value: '10,000+',
            img: '/page-4/solution-configurator-page-4-buildings-07.svg',
          },
        ]
      },
      maturity: {
        title: `Where would you rank your organization's content and process management maturity?`,
        subtitle: 'Select stage or hover over imagery for more',
        component: 'RangeSelector',
        content: [
          {
            value: '0',
            img: '/page-5/solution-configurator-page-5-maturity-steps-full-color-01.svg',
            description: '<strong>Basic:</strong> Content is gathered & used for personal productivity; lack of governance leads to inconsistent information; we define our own tools and workflows'
          },
          {
            value: '1',
            img: '/page-5/solution-configurator-page-5-maturity-steps-full-color-02.svg',
            description: '<strong>Collaborative:</strong> Content is managed around a group or project; content is disconnected from core systems & processes; we benefit from improved productivity through collaboration'
          },
          {
            value: '2',
            img: '/page-5/solution-configurator-page-5-maturity-steps-full-color-03.svg',
            description: '<strong>Integrated:</strong> Content is managed in a core business system; departmental governance improves consistency and usage; menial tasks have been automated'
          },
          {
            value: '3',
            img: '/page-5/solution-configurator-page-5-maturity-steps-full-color-04.svg',
            description: '<strong>Aligned:</strong> Content is managed in alignment with business imperatives; governance is formalized across the enterprise; we access content contextually relevant to tasks'
          },
          {
            value: '4',
            img: '/page-5/solution-configurator-page-5-maturity-steps-full-color-05.svg',
            description: '<strong>Transformative:</strong> Content helps transform customer experience & streamline internal process; governance enables consistency and control; we have flexible technology that enables dynamic management'
          },
        ],
      },
      role: {
        title: `How do you interact with your company’s tech solutions?`,
        subtitle: `Select the description that aligns with your primary responsibility`,
        component: 'RadioCarousel',
        isRadio: true,
        content: [
          {
            img: '/page-6/solution-configurator-page-6-company-roles-01.svg',
            alt: 'I decide which systems we use',
            category: 'role',
            value: 'decisionMaker'
          },
          {
            img: '/page-6/solution-configurator-page-6-company-roles-03.svg',
            alt: 'I’m a developer or enterprise architect',
            category: 'role',
            value: 'developer'
          },
          {
            img: '/page-6/solution-configurator-page-6-company-roles-02.svg',
            alt: 'I manage people who use the systems',
            category: 'role',
            value: 'manager'
          },
          {
            img: '/page-6/solution-configurator-page-6-company-roles-04.svg',
            alt: 'I’m an end user',
            category: 'role',
            value: 'endUser'
          },
          {
            img: '/page-6/solution-configurator-page-6-company-roles-06.svg',
            alt: 'Other',
            category: 'role',
            value: 'other'
          },
          {
            img: '/page-6/solution-configurator-page-6-company-roles-05.svg',
            alt: 'I train end users',
            category: 'role',
            value: 'systemsTrainer'
          },
        ],
      },
      integrations: {
        title: 'Which core systems does your organization use today?',
        subtitle: 'Select all that apply',
        component: 'Gallery',
        grid: 'grid-x5',
        dynamicContent: 'integrations',
        content: []
      },
      priorities: {
        title: 'What are your priorities when implementing a new solution?',
        subtitle: 'Select your top three',
        component: 'Gallery',
        grid: 'grid-x3',
        limit: 3,
        content: [
          {
            img: '/page-8/solution-configurator-page-8-priorities-01.svg',
            alt: 'Integration',
            category: 'priorities',
            value: 'integration',
          },
          {
            img: '/page-8/solution-configurator-page-8-priorities-02.svg',
            alt: 'Speed',
            category: 'priorities',
            value: 'speed',
          },
          {
            img: '/page-8/solution-configurator-page-8-priorities-03.svg',
            alt: 'Customization',
            category: 'priorities',
            value: 'customization',
          },
          {
            img: '/page-8/solution-configurator-page-8-priorities-04.svg',
            alt: 'Usability',
            category: 'priorities',
            value: 'usability',
          },
          {
            img: '/page-8/solution-configurator-page-8-priorities-05.svg',
            alt: 'Compliance',
            category: 'priorities',
            value: 'compliance',
          },
          {
            img: '/page-8/solution-configurator-page-8-priorities-06.svg',
            alt: 'Cloud-native deployment',
            category: 'priorities',
            value: 'cloudNativeDeployment',
          }
        ],
      },
      timeframe: {
        title: 'When does your organization want to begin transforming its processes?',
        subtitle: 'Select timeframe',
        component: 'RangeSelector',
        content: [
          {
            img: '/page-9/solution-configurator-page-9-timeframe_1.svg',
            value: 'Right away',
          },
          {
            img: '/page-9/solution-configurator-page-9-timeframe_6.svg',
            value: '1-6 months',
          },
          {
            img: '/page-9/solution-configurator-page-9-timeframe_12.svg',
            value: '6-12 months',
          },
          {
            img: '/page-9/solution-configurator-page-9-timeframe_15.svg',
            value: 'More than a year',
          },
          {
            img: '/page-9/solution-configurator-page-9-timeframe_notsure.svg',
            value: 'Not sure',
          },
        ]
      },
      approach: {
        title: 'What is your organization’s primary approach to solution development?',
        subtitle: 'Select one',
        component: 'Gallery',
        grid: 'grid-x3',
        isRadio: true,
        content: [
          {
            img: '/page-10/solution-configurator-page-10-development-01.svg',
            alt: 'We have an in-house team',
            category: 'approach',
            value: 'inHouseTeam',
          },
          {
            img: '/page-10/solution-configurator-page-10-development-02.svg',
            alt: 'We work with a third party',
            category: 'approach',
            value: 'thirdParty',
          },
          {
            img: '/page-10/solution-configurator-page-10-development-03.svg',
            alt: 'We buy software we can use out-of-the-box',
            category: 'approach',
            value: 'buySoftware'
          },
        ]
      },
      results: {
        component: 'Results',
        title: 'Your personalized content and process report'
      },
    },
  },
  getters: {
    collect: (state, category) => {
      let val = [];
      Object.values(state.selectedValues).forEach((value) => {

        if (value.isSelected === true && value.category === category) {
          if (value.category === 'challenge') {
            val.push(value.value);
          } else {
            val = value.value;
          }
        }
      })
      return val;
    },
    get: (state) => (category) => {
      return state.selectedValues[category];
    },
  },
  mutations: {
    mergeData: (state, data) => {
      Object.assign(state.selectedValues, data);
    },
    set(state, category) {
      let val = [];
      Object.values(state.selectedValues).forEach((value) => {
        if (value.isSelected && value.category === category) {
          if (category === "challenges" || category === "integrations" || category === "priorities") {
            if (value.output) {
              val.push(value.output);
            } else if (value.alt) {
              val.push(value.alt);
            } else {
              val.push(value.value);
            }
          }
          else {
            if (value.output) {
              val = value.output;
            } else {
              val = value.value;
            }
          }
        }
      })
      state.selectedValues[category] = val;
    },
    setIntegrations(state) {
      const integrations = {
        Other: ['Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Infor', 'PeopleSoft', 'JD Edwards', 'Deltek', 'Other'],
        Manufacturing: ['Infor', 'PeopleSoft', 'JD Edwards', 'Deltek', 'PTC', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
        Government: ['Esri', 'Cityworks', 'Accela', 'Tempest Prospero', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
        Insurance: ['Duck Creek', 'Guidewire', 'Lifepro', 'Entrata', 'Majesco', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
        'Financial Services': ['Jack Henry', 'Encompass', 'Episys', 'Corelation', 'Fiserv', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
        'Higher Education': ['Ellucian', 'Jenzabar', 'PeopleSoft Campus Solutions', 'Campus Management', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
        Healthcare: ['Epic', 'Allscripts', 'Cerner', 'Salesforce', 'SAP', 'Oracle EBS/Oracle Financials', 'Workday', 'Microsoft', 'Other'],
      }
      const show = integrations[state.selectedValues.industry] || integrations['Other'];
      let arr = [];

      for (let key of Object.keys(state.pages)) {
        if (key === 'integrations') {
          Object.values(show).forEach(value => {
            let valueStr = value.replace(/\W+(?!$)/g, '-').toLowerCase();
            let intContent = {
              alt: value,
              value: valueStr,
              category: 'integrations',
              img: '/page-7/solution-configurator-page-7-logos-' + valueStr + '.jpg',
            }

            arr.push(intContent);

          })

          state.pages[key].content = arr;
        }
      }
    },
    removeAll(state, data) {
      const cardId = data[0];

      Object.entries(state.selectedValues).forEach(([key, value]) => {
        // Removing all selected of same category
        if (value.category === data[1]) {
          value.isSelected = false
        }
        // Reselecting orignally clicked value
        if (key === cardId) {
          value.isSelected = true
        }
      });

      this.commit("set", data[1]);
    }
  },
})